import React, { useEffect, useState } from 'react';
import { Grid, Button } from '@forgeglobal/design-framework';
import { object, string, ref } from 'yup';

import {
  Form,
  Field,
  FormProps,
  FormRenderProps,
  FieldRenderProps,
} from '@tcs/common/components/atoms/Form';
import TextField from '@tcs/common/components/atoms/TextField';
import ValidationWidget from '@tcs/common/components/atoms/ValidationWidget';

import { useStyles } from './style';
import { passwordRules } from '@tcs/client-portal/components/forms/ChangePasswordForm/PasswordRules';

type ChangePasswordFormProps = FormProps & {
  onBackToSignIn: () => void;
};

const EnterNewPasswordForm = ({
  onSubmit,
  onCancel,
  initialValues,
}: ChangePasswordFormProps) => {
  const classes = useStyles();
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordTouched, setNewPasswordTouched] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);

  const onFormValidate = (isValid): void => setIsFormValid(isValid);

  const updateValidationWidget = (e) => {
    setNewPassword((e.target as HTMLInputElement).value);
    setNewPasswordTouched(true);
  };

  let newPasswordSchema = string();

  passwordRules
    .slice()
    .reverse()
    .forEach((rule) => {
      newPasswordSchema = newPasswordSchema.test(
        rule.name,
        rule.message,
        rule.test
      );
    });

  newPasswordSchema = newPasswordSchema.required('Required field');

  const validationSchema = object({
    newPassword: newPasswordSchema,
    confirmNewPassword: string()
      .oneOf(
        [ref('newPassword'), null],
        'Passwords don’t match. Please try again'
      )
      .required('Required field'),
  });

  useEffect(() => {
    // onbeforeunload - browser will handle attempt to navigate away
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    // some input was entered, don't let them leave (back button/reload) without a prompt
    if (newPasswordTouched) {
      window.addEventListener('beforeunload', handler);
    }
    // empty return, not taking further action
    return void 0;
  }, [newPasswordTouched]);

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      onValidate={onFormValidate}
      validationSchema={validationSchema}
      render={({ handleSubmit, submitting }: FormRenderProps) => {
        return (
          validationSchema && (
            <form data-test-id="change-password-form" onSubmit={handleSubmit}>
              <Grid container direction="column" spacing={8}>
                <Grid item>
                  <Grid container direction="column" spacing={4}>
                    <Grid item data-test-id="validation-widget">
                      <ValidationWidget
                        value={newPassword}
                        title="New password must:"
                        rules={passwordRules}
                        touched={newPasswordTouched}
                        additional={[
                          'Password must not match the previous 5 passwords.',
                        ]}
                      />
                    </Grid>
                    <Grid item>
                      <Field
                        name="newPassword"
                        render={({ meta, input }: FieldRenderProps<string>) => (
                          <TextField
                            data-private
                            data-test-id="new-password"
                            type="password"
                            autoComplete="new-password"
                            className={classes.textField}
                            onBlur={(e) => {
                              input.onBlur(e);
                              updateValidationWidget(e);
                            }}
                            onChange={(e) => {
                              input.onChange(e);
                              updateValidationWidget(e);
                            }}
                            label="New password"
                            input={input}
                            meta={{
                              ...meta,
                              /* override form error to prevent duplication */
                              error: undefined,
                            }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item>
                      <Field
                        name="confirmNewPassword"
                        render={(props: FieldRenderProps<string>) => (
                          <TextField
                            data-private
                            data-test-id="confirm-new-password"
                            type="password"
                            label="Confirm new password"
                            autoComplete="new-password"
                            className={classes.textField}
                            {...props}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <div className={classes.buttonsContainer}>
                  <Button
                    data-test-id="cancel-change-password"
                    size="medium"
                    color="secondary"
                    onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button
                    data-test-id="submit-change-password"
                    className={classes.primaryButton}
                    size="medium"
                    color="primary"
                    type="submit"
                    disabled={submitting || !isFormValid}>
                    Save
                  </Button>
                </div>
              </Grid>
            </form>
          )
        );
      }}
    />
  );
};

export default EnterNewPasswordForm;
